/**
 * frame-bus.ts — single in-memory store of per-frame audio state.
 *
 * ARCHITECTURAL FIX for mobile jank:
 *
 *   BEFORE: Every BG canvas called getComputedStyle(root).getPropertyValue("--st-...")
 *           for each of 8 stems on every frame. With 4 canvases at 60 FPS,
 *           that's ~1920 synchronous style-cascade flushes per second — each
 *           one forcing the browser to re-resolve every CSS custom property
 *           cascade on :root. On integrated mobile GPUs this alone was
 *           shipping ~25ms/frame of style work, blowing the 16ms budget.
 *
 *   AFTER: brass-astrolabe.ts pushes the freshly-computed stem levels to
 *          this bus once per audio loop tick. All canvases read from the
 *          bus as plain in-memory numbers — zero DOM cost.
 *
 *   CSS vars are STILL published (for CSS-driven reactivity on compass
 *   elements like .compass-ring, .astrolabe-flower, etc) — that path is
 *   unchanged. Only the JS-side reads are migrated.
 */

import type { StemKey } from "./stem-levels.ts";

export type StemLevels = Readonly<Record<StemKey, number>>;

const stems: Record<StemKey, number> = {
  "lead-vocals": 0,
  "backing-vocals": 0,
  drums: 0,
  bass: 0,
  guitar: 0,
  percussion: 0,
  strings: 0,
  synth: 0,
};

let overall = 0;
let peak = 0;
let loud = 0;

/** Lisa's VOICE level (0..1), published by <meditation-audio>. Distinct
 *  from the soundtrack stems above — this is the guided meditation itself.
 *
 *  Same rationale as the stem bus: <meditation-starfield> used to read this
 *  back off :root with getComputedStyle TWICE per frame while
 *  <meditation-audio> was writing it to :root 30×/s. Write-to-root then
 *  read-computed-style is a forced synchronous style recalculation of the
 *  whole document, every frame, for the entire length of a meditation —
 *  the single hottest loop on the player route. */
let voice = 0;

/** Push new stem levels from the audio analyser. Mutates in-place to
 *  keep allocation cost at zero per frame. */
export const publishStems = (
  next: StemLevels,
  nextOverall: number,
  nextPeak: number,
  nextLoud: number,
): void => {
  stems["lead-vocals"] = next["lead-vocals"];
  stems["backing-vocals"] = next["backing-vocals"];
  stems.drums = next.drums;
  stems.bass = next.bass;
  stems.guitar = next.guitar;
  stems.percussion = next.percussion;
  stems.strings = next.strings;
  stems.synth = next.synth;
  overall = nextOverall;
  peak = nextPeak;
  loud = nextLoud;
};

/** Read a single stem level. O(1), no DOM, no allocations. */
export const getStem = (key: StemKey): number => stems[key];

/** Read all stems as a SHARED reference — DO NOT mutate. Callers that
 *  destructure or copy fields are fine; callers that need a frozen snapshot
 *  should call snapshotStems() instead. */
export const getStems = (): StemLevels => stems as StemLevels;

/** Defensive copy — use when a caller needs a frozen point-in-time snapshot
 *  (e.g. spawning particles that capture levels at birth). */
export const snapshotStems = (): StemLevels => ({
  "lead-vocals": stems["lead-vocals"],
  "backing-vocals": stems["backing-vocals"],
  drums: stems.drums,
  bass: stems.bass,
  guitar: stems.guitar,
  percussion: stems.percussion,
  strings: stems.strings,
  synth: stems.synth,
});

export const getOverall = (): number => overall;
export const getPeak = (): number => peak;
export const getLoud = (): number => loud;

/** Push the meditation voice level. Called from <meditation-audio>'s level
 *  loop (and its decay-to-silence tail) — never from CSS. */
export const publishVoice = (level: number): void => {
  voice = level;
};

/** Read the meditation voice level. O(1), no DOM, no style flush. */
export const getVoice = (): number => voice;
